import type { SoftphoneState } from '@lia/contracts';
import { Call02Icon, Notification01Icon } from '@hugeicons/core-free-icons';
import {
  Button,
  Dialpad,
  Icon,
  IncomingCallCard,
  InlineNotice,
  PresenceBadge,
  PresencePicker,
  Select,
  SOFTPHONE_CALL_STATE_LABELS,
  SOFTPHONE_CALL_STATE_TONE,
  SoftphoneCallPanel,
  SoftphoneDock,
  StatusBadge,
  TransferCallDialog,
  type StatusTone,
  type TransferChoice,
} from '@lia/ui';
import * as React from 'react';
import { Link } from 'react-router';
import { useSoftphone, type SoftphoneController } from '../lib/softphone/softphone-context.js';

// Floating advisor softphone (plan B3), composed of @lia/ui softphone components. Honest by construction: «Conectado»
// only when the SDK is registered AND the server sees the softphone; a call is «En llamada» only when the server says
// `connected`; the provider capability stays «Sin verificar» until a real call completes.

const VERIFICATION: Record<SoftphoneState['verification']['state'], { tone: StatusTone; label: string }> = {
  verified: { tone: 'success', label: 'Verificado' },
  unverified: { tone: 'warning', label: 'Sin verificar' },
  failed: { tone: 'danger', label: 'Verificación fallida' },
  unsupported: { tone: 'neutral', label: 'No disponible' },
  unknown: { tone: 'warning', label: 'Sin verificar' },
};

export function SoftphoneWidget({ workspaceId }: { workspaceId: string }) {
  const sp = useSoftphone();
  if (!sp?.state) return null;
  // No seat and no call: nothing floats over the page.
  if (!sp.state.seat.enabled && !sp.state.call) return null;
  return <SoftphoneWidgetView sp={sp} state={sp.state} workspaceId={workspaceId} />;
}

function connectionBadge(sp: SoftphoneController, state: SoftphoneState): { tone: StatusTone; label: string } {
  if (sp.sdk === 'connecting') return { tone: 'info', label: 'Conectando…' };
  if (sp.sdk === 'ready') return state.presence === 'offline' ? { tone: 'info', label: 'Registrando…' } : { tone: 'success', label: 'Conectado' };
  if (sp.sdk === 'disconnected') return { tone: 'danger', label: 'Conexión perdida' };
  if (sp.sdk === 'error') return { tone: 'danger', label: 'Sin conectar' };
  return { tone: 'neutral', label: 'Sin conectar' };
}

function SoftphoneWidgetView({ sp, state, workspaceId }: { sp: SoftphoneController; state: SoftphoneState; workspaceId: string }) {
  const [open, setOpen] = React.useState(false);
  const [number, setNumber] = React.useState('');
  const [line, setLine] = React.useState<string | undefined>(undefined);
  const [transferOpen, setTransferOpen] = React.useState(false);
  const [transferBusy, setTransferBusy] = React.useState(false);
  const [transferError, setTransferError] = React.useState<string | null>(null);
  const call = state.call;
  const live = !!call && call.state !== 'ended';
  const ringing = !!sp.incoming;

  // Open the panel for anything that needs the advisor's attention.
  React.useEffect(() => {
    if (ringing || sp.pendingOutbound || sp.placing) setOpen(true);
  }, [ringing, sp.pendingOutbound, sp.placing]);

  const lineId = line && state.lines.some((l) => l.endpointId === line) ? line : state.lines[0]?.endpointId;
  const conn = connectionBadge(sp, state);
  const verification = VERIFICATION[state.verification.state];
  const blocker = state.seat.blocker;
  const ready = sp.sdk === 'ready';
  const ringingCall = call && call.direction === 'inbound' && call.state === 'ringing' ? call : null;

  const launcherStatus = live ? (
    <StatusBadge tone={SOFTPHONE_CALL_STATE_TONE[call.state]}>{SOFTPHONE_CALL_STATE_LABELS[call.state]}</StatusBadge>
  ) : ringing ? (
    <StatusBadge tone="warning">Llamada entrante</StatusBadge>
  ) : ready && state.presence !== 'offline' ? (
    <PresenceBadge presence={state.presence} />
  ) : (
    <StatusBadge tone={conn.tone}>{conn.label}</StatusBadge>
  );

  const copilot = call ? (
    call.copilotSessionId ? (
      <Button asChild variant="link" size="sm" className="justify-start">
        <Link to={`/w/${workspaceId}/copilot/sessions/${call.copilotSessionId}`}>Abrir copiloto en vivo</Link>
      </Button>
    ) : call.copilotDetail ? (
      <p className="text-meta text-text-secondary">Copiloto: {call.copilotDetail}</p>
    ) : null
  ) : null;

  const submitTransfer = async (choice: TransferChoice) => {
    setTransferBusy(true);
    setTransferError(null);
    const err = await sp.transfer(choice);
    setTransferBusy(false);
    if (err) setTransferError(err);
    else setTransferOpen(false);
  };

  return (
    <SoftphoneDock open={open} onOpenChange={setOpen} status={launcherStatus} attention={ringing}>
      <div className="grid gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5" role="status" aria-live="polite">
          <StatusBadge tone={conn.tone}>{conn.label}</StatusBadge>
          <PresenceBadge presence={state.presence} />
          <StatusBadge tone={verification.tone}>{verification.label}</StatusBadge>
        </div>
        <p className="text-meta text-text-secondary">{state.verification.detail}</p>
      </div>

      {blocker ? (
        <InlineNotice tone="warning" role="note">
          {blocker}
        </InlineNotice>
      ) : null}
      {sp.error ? (
        <InlineNotice tone="danger" role="alert">
          {sp.error}
        </InlineNotice>
      ) : null}
      {sp.actionError ? (
        <InlineNotice tone="danger" role="alert">
          {sp.actionError}
        </InlineNotice>
      ) : null}

      {!blocker && !ready ? (
        <div className="grid gap-1">
          <Button leadingIcon={<Icon icon={Call02Icon} aria-hidden />} loading={sp.sdk === 'connecting'} onClick={() => void sp.connect()}>
            {sp.sdk === 'disconnected' || sp.sdk === 'error' ? 'Reconectar softphone' : 'Conectar softphone'}
          </Button>
          <p className="text-meta text-text-secondary">El navegador te pedirá permiso para usar el micrófono.</p>
        </div>
      ) : null}

      {!blocker && ready ? (
        <div className="flex flex-wrap items-center gap-2">
          <PresencePicker value={state.chosenPresence} onValueChange={(p) => void sp.setPresence(p)} className="w-40" />
          <Button size="sm" variant="ghost" onClick={() => void sp.disconnect()} disabled={live}>
            Desconectar
          </Button>
          {sp.notifications.supported && sp.notifications.permission === 'default' ? (
            <Button size="sm" variant="ghost" leadingIcon={<Icon icon={Notification01Icon} aria-hidden />} onClick={() => void sp.notifications.enable()}>
              Activar avisos del navegador
            </Button>
          ) : null}
        </div>
      ) : null}

      {sp.incoming ? (
        <IncomingCallCard
          callerName={ringingCall?.contactName}
          callerNumber={ringingCall?.counterpartE164 ?? sp.incoming.remoteNumber}
          lineName={ringingCall?.lineName}
          onAnswer={() => void sp.answer()}
          onReject={() => void sp.reject()}
        />
      ) : null}

      {call && !(sp.incoming && call.state === 'ringing') ? (
        <SoftphoneCallPanel
          state={call.state}
          direction={call.direction}
          contactName={call.contactName}
          number={call.counterpartE164}
          lineName={call.lineName}
          answeredAt={call.answeredAt}
          endedAt={call.endedAt}
          endReason={call.endReason}
          muted={sp.muted}
          held={sp.held}
          onToggleMute={() => sp.setMuted(!sp.muted)}
          onToggleHold={() => void sp.setHeld(!sp.held)}
          onTransfer={() => {
            setTransferError(null);
            setTransferOpen(true);
          }}
          onHangup={() => void sp.hangup()}
          onDigit={sp.sendDtmf}
          transfer={call.transfer}
          copilot={copilot}
        />
      ) : null}

      {!blocker && ready && !live && !sp.incoming ? (
        state.lines.length === 0 ? (
          <InlineNotice tone="info" role="note">
            No tienes líneas de Telnyx activas para llamar. Pide a un administrador que asigne una línea a tus proyectos.
          </InlineNotice>
        ) : (
          <div className="grid gap-3">
            <Select
              aria-label="Línea para llamar"
              value={lineId}
              onValueChange={setLine}
              options={state.lines.map((l) => ({ value: l.endpointId, label: `${l.displayName} · ${l.phoneE164}` }))}
            />
            <Dialpad
              value={number}
              onValueChange={setNumber}
              calling={sp.placing}
              onCall={(to) => {
                sp.clearActionError();
                void sp.call({ to, endpointId: lineId }).then((ok) => ok && setNumber(''));
              }}
            />
          </div>
        )
      ) : null}

      <TransferCallDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        targets={state.transferTargets.map((t) => ({ id: t.membershipId, name: t.name, presence: t.presence }))}
        onTransfer={(c) => void submitTransfer(c)}
        busy={transferBusy}
        error={transferError}
      />
    </SoftphoneDock>
  );
}

/** Click-to-call through the advisor's softphone. Rendered only when the advisor's seat is usable. */
export function SoftphoneCallButton({ contactId, label = 'Llamar con softphone' }: { contactId: string; label?: string }) {
  const sp = useSoftphone();
  if (!sp?.seatUsable) return null;
  const live = !!sp.state?.call && sp.state.call.state !== 'ended';
  return (
    <Button
      size="sm"
      variant="secondary"
      leadingIcon={<Icon icon={Call02Icon} aria-hidden />}
      loading={sp.placing || sp.sdk === 'connecting'}
      disabled={live || !!sp.incoming}
      title={live ? 'Ya tienes una llamada en curso' : undefined}
      onClick={() => void sp.call({ contactId })}
    >
      {label}
    </Button>
  );
}
